import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useTranslation, Trans } from 'react-i18next';
import { Select } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { Button, EmailInput, PasswordInput, Checkbox } from '../../components/atoms';
import { AuthHero } from '../../components/organisms';
import heroImage from '../../assets/images/vet-hero.webp';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] = useState(false);

  const { t, i18n } = useTranslation();
  const { login, loginWithGoogle, isLoading, error, isAuthenticated, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if Google Client ID is available
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const isGoogleLoginEnabled = !!googleClientId && googleClientId !== 'your-google-client-id-here';

  // Wait for Google script to load
  useEffect(() => {
    if (isGoogleLoginEnabled) {
      const timer = setTimeout(() => {
        setIsGoogleScriptLoaded(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isGoogleLoginEnabled]);

  // Get redirect path from location state
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      setShowSuccessAnimation(true);
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 1000);
    }
  }, [isAuthenticated, navigate, from]);

  // Update general error when auth error changes
  useEffect(() => {
    if (error) {
      setGeneralError(error);
    }
  }, [error]);

  // Handle email change
  const handleEmailChange = useCallback((value: string, isValid: boolean) => {
    setEmail(value);
    setIsEmailValid(isValid);
    clearError();
    setGeneralError('');
  }, [clearError]);

  // Handle password change
  const handlePasswordChange = useCallback((value: string, isValid: boolean) => {
    setPassword(value);
    setIsPasswordValid(isValid);
    clearError();
    setGeneralError('');
  }, [clearError]);

  // Custom email validator with i18n
  const emailValidator = useCallback(
    (email: string): { isValid: boolean; message: string } => {
      if (!email) {
        return { isValid: false, message: t('auth.login.validation.emailRequired') };
      }

      if (email.length < 3) {
        return { isValid: false, message: t('auth.login.validation.emailFormat') };
      }

      if (!email.includes('@') || !email.includes('.')) {
        return { isValid: false, message: t('auth.login.validation.emailFormat') };
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return { isValid: false, message: t('auth.login.validation.emailFormat') };
      }

      return { isValid: true, message: '' };
    },
    [t]
  );

  // Custom password validator with i18n
  const passwordValidator = useCallback(
    (password: string): { isValid: boolean; message: string } => {
      if (!password) {
        return { isValid: false, message: t('auth.login.validation.passwordRequired') };
      }

      if (password.length < 6) {
        return { isValid: false, message: t('auth.login.validation.passwordLength') };
      }

      return { isValid: true, message: '' };
    },
    [t]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setGeneralError('');

    if (!isEmailValid || !isPasswordValid) {
      setGeneralError(t('auth.login.validation.formInvalid', { defaultValue: 'Please fix all errors before submitting' }));
      return;
    }

    try {
      await login({ email, password });
      // Success animation and navigation handled by useEffect
    } catch (error) {
      // Error is handled by the auth context and useEffect
      console.error('Login failed:', error);
    }
  };

  // Handle Google login success
  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      if (credentialResponse.credential) {
        await loginWithGoogle(credentialResponse.credential);
      }
    } catch (error: any) {
      console.error('Google login failed:', error);
      setGeneralError(error.response?.data?.message || t('auth.login.googleError'));
    }
  };

  // Handle Google login error
  const handleGoogleError = (error: any) => {
    console.error('Google login error:', error);

    let errorMessage = t('auth.login.googleError');

    // Provide more specific error messages
    if (error?.error === 'popup_closed_by_user') {
      errorMessage = 'Google sign-in popup was closed before completion.';
    } else if (error?.error === 'access_denied') {
      errorMessage = 'Access denied. Please grant permission to continue.';
    } else if (error?.error?.includes('origin')) {
      errorMessage = 'Google OAuth configuration error. Please contact administrator.';
    }

    setGeneralError(errorMessage);
  };

  return (
    <div className="relative grid min-h-screen bg-base-200 text-base-content md:grid-cols-[1.15fr,1fr]">
      <AuthHero
        eyebrow={t('auth.login.heroEyebrow')}
        headline={t('auth.login.heroHeadline')}
        description={t('auth.login.heroDescription')}
        image={heroImage}
        imageAlt="VetPintar Hero"
      />

      <div className="relative flex min-h-screen flex-col justify-center px-6 py-12 sm:px-12" style={{ background: 'linear-gradient(135deg, #FAFBFC 0%, #F8F9FD 50%, #FAFBFC 100%)' }}>
        <div className="absolute right-6 top-6 sm:right-12 z-20">
          <Select
            value={i18n.language}
            onChange={(value) => i18n.changeLanguage(value)}
            className="w-32"
            options={[
              { value: 'id', label: t('language.indonesian') },
              { value: 'en', label: t('language.english') }
            ]}
          />
        </div>
        {showSuccessAnimation && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/95 backdrop-blur-md animate-fade-in">
            <div className="flex min-w-[300px] flex-col items-center gap-5 rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-soft animate-scale-in">
              <div className="inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-emerald-500/15 text-3xl text-emerald-600 shadow-sm">
                ✓
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-800">{t('auth.login.loadingTitle')}</h3>
                <p className="mt-2 text-sm text-slate-600">{t('auth.login.loadingDescription')}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mx-auto w-full max-w-md space-y-6">
          <div className="space-y-3 text-center">
            <span className="inline-block rounded-full border border-indigo-600/30 bg-indigo-100/60 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.3em] text-indigo-600">
              {t('common.appName')}
            </span>
            <h2 className="text-3xl font-semibold text-slate-800">{t('auth.login.title')}</h2>
            <p className="text-sm text-slate-600">{t('auth.login.subtitle')}</p>
          </div>

          <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-soft">
            {/* Subtle gradient overlay matching dashboard */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-50/30 via-transparent to-cyan-50/20" />
            <div className="relative space-y-6">
              {generalError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                  <p className="font-semibold">{t('auth.login.errorTitle')}</p>
                  <p className="mt-1">{generalError}</p>
                </div>
              )}

              <form className="space-y-5" onSubmit={handleSubmit} noValidate>
                <EmailInput
                  id="email"
                  label={t('auth.login.emailLabel')}
                  value={email}
                  onChange={handleEmailChange}
                  disabled={isLoading}
                  placeholder={t('auth.login.emailPlaceholder')}
                  required
                  showValidation
                  validateOnChange
                  validateOnBlur
                  customValidator={emailValidator}
                />

                <PasswordInput
                  id="password"
                  label={t('auth.login.passwordLabel')}
                  value={password}
                  onChange={handlePasswordChange}
                  disabled={isLoading}
                  placeholder={t('auth.login.passwordPlaceholder')}
                  required
                  showValidation
                  validateOnChange
                  validateOnBlur
                  customValidator={passwordValidator}
                />

                <div className="flex items-center justify-between text-sm">
                  <Checkbox
                    label={t('auth.login.rememberMe')}
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading}
                    className="[&_.ant-checkbox-inner]:border-slate-300"
                  />
                  <Link
                    to="/forgot-password"
                    className="font-semibold text-indigo-600 transition-colors hover:text-indigo-700"
                  >
                    {t('auth.login.forgotPassword')}
                  </Link>
                </div>

                <Button
                  htmlType="submit"
                  isLoading={isLoading}
                  fullWidth
                  size="large"
                  className="h-12 rounded-xl text-sm font-semibold"
                >
                  {isLoading ? t('auth.login.submitting') : t('auth.login.submit')}
                </Button>
              </form>

              <div className="divider text-xs font-medium uppercase tracking-wider text-slate-400">
                {t('auth.login.divider')}
              </div>

              <div className="flex justify-center">
                {isGoogleLoginEnabled ? (
                  isGoogleScriptLoaded ? (
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => handleGoogleError({ error: 'google_login_error' })}
                      theme="outline"
                      size="large"
                      text="signin_with"
                      width="400"
                    />
                  ) : (
                    <div className="flex h-[44px] w-[400px] items-center justify-center rounded border border-slate-200">
                      <span className="loading loading-spinner loading-sm"></span>
                      <span className="ml-2 text-sm text-slate-600">Loading Google Sign-In...</span>
                    </div>
                  )
                ) : (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 shadow-sm">
                    <p className="font-semibold">Google Login Not Available</p>
                    <p className="mt-1 text-amber-700">Google OAuth needs to be configured. Please check GOOGLE_OAUTH_SETUP.md for instructions.</p>
                  </div>
                )}
              </div>

              <p className="text-center text-sm text-slate-600">
                {t('auth.login.cta.prompt')}{' '}
                <Link
                  to="/register"
                  className="font-semibold text-indigo-600 transition-colors hover:text-indigo-700"
                >
                  {t('auth.login.cta.action')}
                </Link>
              </p>

              <p className="text-center text-xs text-slate-500">
                <Trans
                  i18nKey="auth.login.terms"
                  components={[
                    <a key="terms" className="text-slate-600 transition-colors hover:text-slate-900 hover:underline" href="/terms">{' '}</a>,
                    <a key="privacy" className="text-slate-600 transition-colors hover:text-slate-900 hover:underline" href="/privacy">{' '}</a>
                  ]}
                />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
